import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Box,
  Container,
  Grid,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import CustomButton from "../../../components/CustomButton";
import CustomCheckbox from "../../../components/CustomCheckbox";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomTable from "../../../components/CustomTable";
import CustomTextField from "../../../components/CustomTextField";
import CustomSelect from "../../../components/Select/Select";
import TopBar from "../../../components/TopBar/TopBar";
import { getAdministeredConsumablesReport } from "../../../redux/reducers/clinicSlice";
import { getApprovalRules } from "../../../redux/reducers/globalSettingsSlice";
import { canWriteModule } from "../../../util/roleModuleAccess";
import { baseURL } from "../../../util/server";
import StockTransactionHistoryModal from "../../CommonScreens/Stock/StockTransactionHistoryModal";
import AdjustmentHistory from "./AdjustmentHistory";
import Bin from "./Bin";
import Main from "./Dashboard.js/Main";
import InventoryDashboard from "./InventoryDashboard";
import InventorySettingsCategory from "./InventorySettingsCategory";
import Location from "./Location";
import LocationCategory from "./LocationCategory";
import FastSlowMoving from "./FastSlowMoving";
import PendingReview from "./PendingReview";
import PurchaseInvoice from "./PurchaseInvoice";
import PurchaseOrders from "./PurchaseOrders";
import ReorderList from "./ReorderList";
import Service from "./Service";
import Stock from "./Stock";
import Main2 from "./StockDashboard.js/Main2";
import StockPendingTable from "./StockPendingTable";
import StockRequest from "./StockRequest";
import Supplier from "./Supplier";

const subTabsToShow = ["Stock", "Purchase", "Adjustments & Transfers"];

const stockList = ["All Stock", "Stock Insights"];
const stockInsightsList = [
  "Low Stock",
  "Near Expiry",
  "Out of Stock",
  "Reorder List",
  "Fast/Slow Moving",
];
const purchaseList = ["Purchase Order", "Purchase Invoice"]; // "Pending to Add"
const adjustmentsTransfersList = ["Transfers", "Adjustments", "Pending Review"];

const salesPurchaseTypeList = [
  { name: "Clinic", value: "clinic" },
  { name: "Pharmacy", value: "pharmacy" },
  { name: "Petshop", value: "petshop" },
];
const salesPurchaseRangeRadios = [
  { label: "Month", value: "Month" },
  { label: "Custom", value: "Custom" },
];
const initSalesPurchaseMod = {
  visible: false,
  type: "clinic",
  rangeType: "Month",
  startDate: null,
  endDate: null,
  typeErr: "",
  startDateErr: "",
  endDateErr: "",
};
const consumptionPeriodRadios = [
  { label: "Today", value: "today" },
  { label: "Week", value: "week" },
  { label: "Month", value: "month" },
  { label: "Custom", value: "custom" },
];
const consumptionColumns = ["productName", "qtyConsumed"];
const initConsumptionMod = {
  visible: false,
  period: "month",
  startDate: null,
  endDate: null,
  startDateErr: "",
  endDateErr: "",
  section: "administered",
  report: null,
};

const getConsumptionDateRange = (period, startDate, endDate) => {
  if (period === "today") {
    const day = moment().format("YYYY-MM-DD");
    return { startDate: day, endDate: day };
  }
  if (period === "week") {
    return {
      startDate: moment().startOf("isoWeek").format("YYYY-MM-DD"),
      endDate: moment().endOf("isoWeek").format("YYYY-MM-DD"),
    };
  }
  if (period === "month") {
    return {
      startDate: moment().startOf("month").format("YYYY-MM-DD"),
      endDate: moment().endOf("month").format("YYYY-MM-DD"),
    };
  }
  const toApiDate = (value) => {
    if (!value) return "";
    if (typeof value.format === "function") return value.format("YYYY-MM-DD");
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
  };
  return {
    startDate: toApiDate(startDate),
    endDate: toApiDate(endDate),
  };
};

const formatConsumptionQty = (qty) => {
  const num = Number(qty);
  return Number.isFinite(num) ? num : (qty ?? "");
};

const ConsumptionReportResult = ({ report, section, onChangeSection }) => {
  const summary = report?.summary ?? {};
  const isConsumables = section === "consumables";
  const rows = (
    isConsumables ? report?.consumables : report?.administered
  )?.map((li) => ({
    ...li,
    qtyConsumed: formatConsumptionQty(li?.totalSalesQty),
  }));
  const qtyConsumed = isConsumables
    ? summary?.consumablesTotalSalesQty
    : summary?.administeredTotalSalesQty;

  const tabStyle = (active) => ({
    width: "auto",
    flex: "0 0 auto",
    padding: "6px 14px",
    borderRadius: 6,
    background: active ? "#f4fff3" : "transparent",
    color: active ? "#0054a6" : "#8d8d8d",
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
  });

  return (
    <div className="mt15">
      <div className="flex-row-between-align-center">
        <div className="flex-row-ali-cen" style={{ gap: 8 }}>
          <div
            style={tabStyle(!isConsumables)}
            onClick={() => onChangeSection("administered")}
          >
            Administered ({summary?.administeredCount ?? 0})
          </div>
          <div
            style={tabStyle(isConsumables)}
            onClick={() => onChangeSection("consumables")}
          >
            Consumables ({summary?.consumablesCount ?? 0})
          </div>
        </div>
        <Typography className="font-medium fs14">
          Qty Consumed: {formatConsumptionQty(qtyConsumed)}
        </Typography>
      </div>
      <div>
        <CustomTable
          grey
          fullWidth
          maxHeight="55vh"
          columns={consumptionColumns}
          datas={rows ?? []}
        />
      </div>
    </div>
  );
};

export const INVENTORY_TAB_PATHS = {
  Dashboard: "dashboard",
  Stock: "stock",
  Service: "service",
  Purchase: "purchase",
  "Adjustments & Transfers": "adjustments-transfers",
  // legacy aliases
  "Request & Transfer": "adjustments-transfers",
  "Returns & Adjustments": "adjustments-transfers",
  Report: "report",
  Master: "master",
};

export const INVENTORY_PATH_TO_TAB = {
  dashboard: "Dashboard",
  stock: "Stock",
  service: "Service",
  purchase: "Purchase",
  "adjustments-transfers": "Adjustments & Transfers",
  // legacy aliases
  "request-transfer": "Adjustments & Transfers",
  "returns-adjustments": "Adjustments & Transfers",
  report: "Report",
  master: "Master",
};

export const INVENTORY_SUB_TAB_PATHS = {
  "All Stock": "all-stock",
  "Low Stock": "low-stock",
  "Near Expiry": "near-expiry",
  "Out of Stock": "out-of-stock",
  "Reorder List": "reorder-list",
  "Fast/Slow Moving": "fast-slow-moving",
  "Purchase Order": "purchase-orders",
  "Purchase Invoice": "purchase-invoice",
  "Pending to Add": "pending-to-add",
  Transfers: "transfers",
  Adjustments: "adjustments",
  "Pending Review": "pending-review",
  Stock: "stock",
  Service: "service",
  Services: "services",
  Location: "location",
  Supplier: "supplier",
  "Inventory Settings": "inventory-settings",
};

export const INVENTORY_PATH_TO_SUB_TAB = {
  "all-stock": "All Stock",
  "low-stock": "Low Stock",
  "near-expiry": "Near Expiry",
  "out-of-stock": "Out of Stock",
  "reorder-list": "Reorder List",
  "fast-slow-moving": "Fast/Slow Moving",
  "purchase-orders": "Purchase Order",
  "purchase-invoice": "Purchase Invoice",
  "pending-to-add": "Pending to Add",
  transfers: "Transfers",
  "adjustment-history": "Adjustments",
  adjustments: "Adjustments",
  "pending-review": "Pending Review",
  stock: "Stock",
  service: "Service",
  services: "Services",
  location: "Location",
  supplier: "Supplier",
  "inventory-settings": "Inventory Settings",
};

export const InventoryRedirect = () => {
  const location = useLocation();
  const tabFromState = location.state?.activeTab ?? location.state?.selectedTab;
  const path =
    INVENTORY_TAB_PATHS[tabFromState] ?? INVENTORY_TAB_PATHS.Dashboard;
  const tabName = INVENTORY_PATH_TO_TAB[path];
  const defaultSub =
    tabName === "Report"
      ? "Stock"
      : tabName === "Master"
        ? "Location"
        : tabName === "Stock"
          ? "All Stock"
          : tabName === "Purchase"
            ? "Purchase Order"
            : tabName === "Adjustments & Transfers"
              ? "Transfers"
              : null;
  const subPath = defaultSub ? INVENTORY_SUB_TAB_PATHS[defaultSub] : null;
  return (
    <Navigate
      to={subPath ? `/inventory/${path}/${subPath}` : `/inventory/${path}`}
      replace
      state={location.state}
    />
  );
};

const Inventory = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { tab, subTab: subTabParam } = useParams();
  const activeTab = INVENTORY_PATH_TO_TAB[tab];
  const fetchRef = useRef(false);
  const consumptionReqRef = useRef(0);
  const [penTblVsble, setPenTblVsble] = useState(false);
  const { pendingStocks } = useSelector((state) => state?.clinic);
  const [anchorEl, setAnchorEl] = useState(null);
  const [serviceOption, setServiceOption] = useState("Service");
  const [stockOption, setStockOption] = useState("Stock");
  const [serviceAnchorEl, setServiceAnchorEl] = useState(null);
  const [stockAnchorEl, setStockAnchorEl] = useState(null);
  const [masterAnchorEl, setMasterAnchorEl] = useState(null);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const [reportAnchorEl, setReportAnchorEl] = useState(null);
  const [locationInnerTab, setLocationInnerTab] = useState("Bin");
  const [inventorySettingsInnerTab, setInventorySettingsInnerTab] =
    useState("Stock");
  const [stockTransModVsble, setStockTransModVsble] = useState(false);
  const [salesPurchaseModValues, setSalesPurchaseModValues] =
    useState(initSalesPurchaseMod);
  const [consumptionModValues, setConsumptionModValues] =
    useState(initConsumptionMod);
  const role = localStorage.getItem("role");
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const canManageInventory = canWriteModule("inventory");
  const userRaw = localStorage.getItem("user");

  useEffect(() => {
    if (activeTab !== "Adjustments & Transfers") return;
    dispatch(getApprovalRules({ silent: true }));
  }, [activeTab, dispatch]);

  const { clinic } = useMemo(() => {
    if (!mandateDataRaw) {
      return { clinic: {} };
    }
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        clinic: reqData?.clinic ?? {},
      };
    } catch {
      return { clinic: {} };
    }
  }, [mandateDataRaw]);

  const clinicId = useMemo(() => {
    try {
      const user = userRaw ? JSON.parse(userRaw) : {};
      return user?.activeBranchId ?? clinic?.id ?? null;
    } catch {
      return clinic?.id ?? null;
    }
  }, [userRaw, clinic?.id]);

  useEffect(() => {
    if (!consumptionModValues?.visible || !clinicId) return;
    const period = consumptionModValues?.period || "month";
    if (
      period === "custom" &&
      (!consumptionModValues?.startDate || !consumptionModValues?.endDate)
    ) {
      return;
    }

    const { startDate, endDate } = getConsumptionDateRange(
      period,
      consumptionModValues?.startDate,
      consumptionModValues?.endDate,
    );
    const reqId = ++consumptionReqRef.current;
    dispatch(
      getAdministeredConsumablesReport({
        clinicId,
        period,
        startDate,
        endDate,
      }),
    ).then((res) => {
      if (reqId !== consumptionReqRef.current) return;
      if (res?.payload) {
        setConsumptionModValues((prev) => ({
          ...prev,
          report: res.payload,
        }));
      }
    });
  }, [
    consumptionModValues?.visible,
    consumptionModValues?.period,
    consumptionModValues?.period === "custom"
      ? consumptionModValues?.startDate
      : null,
    consumptionModValues?.period === "custom"
      ? consumptionModValues?.endDate
      : null,
    clinicId,
    dispatch,
  ]);

  const getTabBasedSubList = (tabName) => {
    if (!subTabsToShow.includes(tabName)) return null;
    if (tabName === "Stock") return stockList;
    if (tabName === "Purchase") return purchaseList;
    if (tabName === "Adjustments & Transfers") return adjustmentsTransfersList;
    return null;
  };

  const getDefaultSubTab = (tabName) => {
    if (tabName === "Report") return "Stock";
    if (tabName === "Master") return "Location";
    const subTabs = getTabBasedSubList(tabName);
    return subTabs?.length > 0 ? subTabs[0] : null;
  };

  // Redirect legacy tab paths to the merged Adjustments & Transfers route
  if (tab === "request-transfer" || tab === "returns-adjustments") {
    const legacySub =
      tab === "returns-adjustments"
        ? (INVENTORY_SUB_TAB_PATHS[
            INVENTORY_PATH_TO_SUB_TAB[subTabParam] ?? "Adjustments"
          ] ?? INVENTORY_SUB_TAB_PATHS.Adjustments)
        : INVENTORY_SUB_TAB_PATHS.Transfers;
    return (
      <Navigate
        to={`/inventory/${INVENTORY_TAB_PATHS["Adjustments & Transfers"]}/${legacySub}`}
        replace
        state={location.state}
      />
    );
  }

  const activeSubTab =
    INVENTORY_PATH_TO_SUB_TAB[subTabParam] ??
    getDefaultSubTab(activeTab) ??
    null;

  const navigateToTab = (nextTab, nextSubTab = undefined) => {
    const path = INVENTORY_TAB_PATHS[nextTab];
    if (!path) {
      navigate(`/inventory/${INVENTORY_TAB_PATHS.Dashboard}`, {
        state: location.state,
      });
      return;
    }
    const resolvedSub =
      nextSubTab === undefined ? getDefaultSubTab(nextTab) : nextSubTab;
    const subPath = resolvedSub ? INVENTORY_SUB_TAB_PATHS[resolvedSub] : null;
    navigate(subPath ? `/inventory/${path}/${subPath}` : `/inventory/${path}`, {
      state: location.state,
    });
  };

  const setActiveTab = (nextTab, nextSubTab) => {
    if (!nextTab) return;
    navigateToTab(nextTab, nextSubTab);
  };

  const setActiveSubTab = (nextSubTab) => {
    if (nextSubTab === null || nextSubTab === undefined) {
      if (activeTab) navigateToTab(activeTab, null);
      return;
    }
    if (!activeTab) return;
    navigateToTab(activeTab, nextSubTab);
  };

  const handlePending = () => {
    setPenTblVsble(true);
    navigateToTab("Stock");
  };

  const handleChangeTab = (tabName) => {
    navigateToTab(tabName);
  };

  const onSubTabChange = (st) => {
    navigateToTab(activeTab, st);
  };

  const onOpenStockTransReport = () => {
    setStockTransModVsble(true);
    setDownloadAnchorEl(null);
  };

  const onOpenSalesPurchaseReport = () => {
    setSalesPurchaseModValues({
      ...initSalesPurchaseMod,
      visible: true,
      startDate: moment().startOf("month").toDate(),
      endDate: new Date(),
    });
    setDownloadAnchorEl(null);
  };

  const onCloseSalesPurchaseMod = () => {
    setSalesPurchaseModValues(initSalesPurchaseMod);
  };

  const onChangeSalesPurchase = (name, value) => {
    const reqObj = {
      ...salesPurchaseModValues,
      [name]: value,
      [`${name}Err`]: "",
    };
    if (name === "rangeType" && value === "Month") {
      reqObj.startDateErr = "";
      reqObj.endDateErr = "";
    }
    setSalesPurchaseModValues(reqObj);
  };

  const onDownloadSalesPurchaseReport = () => {
    const type = salesPurchaseModValues?.type;
    if (!type) {
      return setSalesPurchaseModValues({
        ...salesPurchaseModValues,
        typeErr: "This field is required",
      });
    }

    let startDate = moment().startOf("month").toDate();
    let endDate = new Date();
    if (salesPurchaseModValues?.rangeType === "Custom") {
      if (!salesPurchaseModValues?.startDate) {
        return setSalesPurchaseModValues({
          ...salesPurchaseModValues,
          startDateErr: "This field is required",
        });
      }
      if (!salesPurchaseModValues?.endDate) {
        return setSalesPurchaseModValues({
          ...salesPurchaseModValues,
          endDateErr: "This field is required",
        });
      }
      startDate = new Date(salesPurchaseModValues.startDate);
      endDate = new Date(salesPurchaseModValues.endDate);
    }

    const start = moment(startDate);
    const end = moment(endDate);
    const formattedStart = (start.isBefore(end) ? start : end).format(
      "YYYY-MM-DD",
    );
    const formattedEnd = (start.isBefore(end) ? end : start).format(
      "YYYY-MM-DD",
    );
    const url = `${baseURL}/clinic/stock/sales-purchase-report/export?clinicId=${clinicId}&startDate=${formattedStart}&endDate=${formattedEnd}&type=${type}`;
    window.open(url, "_blank");
    onCloseSalesPurchaseMod();
  };

  const onDownloadStockMaster = () => {
    setDownloadAnchorEl(null);
    if (!clinicId) return;
    const url = `${baseURL}/clinic/stock/export?clinicId=${clinicId}&masterData=true&status=true`;
    window.open(url, "_blank");
  };

  const onDownloadServiceMaster = () => {
    setDownloadAnchorEl(null);
    if (!clinicId) return;
    const url = `${baseURL}/export/service?clinicId=${clinicId}&masterData=true`;
    window.open(url, "_blank");
  };

  const onOpenConsumptionReport = () => {
    setConsumptionModValues({
      ...initConsumptionMod,
      visible: true,
      startDate: moment().startOf("month").toDate(),
      endDate: moment().endOf("month").toDate(),
    });
    setDownloadAnchorEl(null);
  };

  const onCloseConsumptionMod = () => {
    setConsumptionModValues(initConsumptionMod);
  };

  const onDownloadConsumptionReport = () => {
    if (!clinicId) return;
    const period = consumptionModValues?.period || "month";
    if (period === "custom") {
      if (!consumptionModValues?.startDate) {
        return setConsumptionModValues({
          ...consumptionModValues,
          startDateErr: "This field is required",
        });
      }
      if (!consumptionModValues?.endDate) {
        return setConsumptionModValues({
          ...consumptionModValues,
          endDateErr: "This field is required",
        });
      }
    }
    const { startDate, endDate } = getConsumptionDateRange(
      period,
      consumptionModValues?.startDate,
      consumptionModValues?.endDate,
    );
    const url = `${baseURL}/clinic/stock/sales-purchase-report/administered-consumables/export?clinicId=${clinicId}&period=${period}&startDate=${startDate}&endDate=${endDate}`;
    window.open(url, "_blank");
  };

  const onChangeConsumption = (name, value) => {
    const reqObj = {
      ...consumptionModValues,
      [name]: value,
      [`${name}Err`]: "",
    };
    if (name === "period" && value !== "custom") {
      reqObj.startDateErr = "";
      reqObj.endDateErr = "";
    }
    if (name === "period" || name === "startDate" || name === "endDate") {
      reqObj.report = null;
    }
    setConsumptionModValues(reqObj);
  };

  const onCloseStockTransMod = () => {
    setStockTransModVsble(false);
  };

  if (!activeTab) {
    return (
      <Navigate
        to={`/inventory/${INVENTORY_TAB_PATHS.Dashboard}`}
        replace
        state={location.state}
      />
    );
  }

  return (
    <>
      <TopBar>
        <Container maxWidth="xl">
          <div className="flex-row-ali-cen">
            <div
              className="ph10pv3"
              onClick={() => handleChangeTab("Dashboard")}
            >
              <div
                className={`flex-center cursor ${
                  activeTab === "Dashboard"
                    ? "pet-det-sel-tab"
                    : "pet-det-un-sel-tab"
                }`}
              >
                Dashboard
              </div>
            </div>
            <div className="ph10pv3" onClick={() => handleChangeTab("Stock")}>
              <div
                className={`flex-center cursor ${
                  activeTab === "Stock"
                    ? "pet-det-sel-tab"
                    : "pet-det-un-sel-tab"
                }`}
              >
                Stock
              </div>
            </div>
            <div className="ph10pv3" onClick={() => handleChangeTab("Service")}>
              <div
                className={`flex-center cursor ${
                  activeTab === "Service"
                    ? "pet-det-sel-tab"
                    : "pet-det-un-sel-tab"
                }`}
              >
                Service
              </div>
            </div>
            <div
              className="ph10pv3"
              onClick={() => handleChangeTab("Purchase")}
            >
              <div
                className={`flex-center cursor ${
                  activeTab === "Purchase"
                    ? "pet-det-sel-tab"
                    : "pet-det-un-sel-tab"
                }`}
              >
                Purchase
              </div>
            </div>
            <div
              className="ph10pv3"
              onClick={() => handleChangeTab("Adjustments & Transfers")}
            >
              <div
                className={`flex-center cursor ${
                  activeTab === "Adjustments & Transfers"
                    ? "pet-det-sel-tab"
                    : "pet-det-un-sel-tab"
                }`}
              >
                Adjustments & Transfers
              </div>
            </div>
            {/* <div>
              <div
                onMouseEnter={(e) => {
                  setReportAnchorEl(e.currentTarget);
                }}
                className={`flex-center cursor ${
                  activeTab === "Report"
                    ? "pet-det-sel-tab"
                    : "pet-det-un-sel-tab"
                }`}
              >
                Report <ArrowDropDownIcon fontSize="small" />
              </div>
              <Menu
                anchorEl={reportAnchorEl}
                open={Boolean(reportAnchorEl)}
                onClose={() => setReportAnchorEl(null)}
                MenuListProps={{
                  onMouseLeave: () => setReportAnchorEl(null),
                }}
              >
                <MenuItem
                  onClick={() => {
                    setReportAnchorEl(null);
                    navigateToTab("Report", "Stock");
                  }}
                >
                  Stock
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setReportAnchorEl(null);
                    navigateToTab("Report", "Service");
                  }}
                >
                  Service
                </MenuItem>
              </Menu>
            </div> */}
            <div>
              <div
                onMouseEnter={(e) => {
                  setMasterAnchorEl(e.currentTarget);
                }}
                className={`flex-center cursor ${
                  activeTab === "Master"
                    ? "pet-det-sel-tab"
                    : "pet-det-un-sel-tab"
                }`}
              >
                Master <ArrowDropDownIcon fontSize="small" />
              </div>
              <Menu
                anchorEl={masterAnchorEl}
                open={Boolean(masterAnchorEl)}
                onClose={() => setMasterAnchorEl(null)}
                MenuListProps={{
                  onMouseLeave: () => setMasterAnchorEl(null),
                }}
              >
                <MenuItem
                  onClick={() => {
                    setMasterAnchorEl(null);
                    setLocationInnerTab("Bin");
                    navigateToTab("Master", "Location");
                  }}
                >
                  Location
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setMasterAnchorEl(null);
                    navigateToTab("Master", "Supplier");
                  }}
                >
                  Supplier
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setMasterAnchorEl(null);
                    setInventorySettingsInnerTab("Stock");
                    navigateToTab("Master", "Inventory Settings");
                  }}
                >
                  Inventory Settings
                </MenuItem>
              </Menu>
            </div>
            {canManageInventory ? (
              <div>
                <div
                  onMouseEnter={(e) => {
                    setDownloadAnchorEl(e.currentTarget);
                  }}
                  className={`flex-center cursor ${
                    activeTab === "Download Reports"
                      ? "pet-det-sel-tab"
                      : "pet-det-un-sel-tab"
                  }`}
                >
                  Download Reports <ArrowDropDownIcon fontSize="small" />
                </div>
                <Menu
                  anchorEl={downloadAnchorEl}
                  open={Boolean(downloadAnchorEl)}
                  onClose={() => setDownloadAnchorEl(null)}
                  MenuListProps={{
                    onMouseLeave: () => setDownloadAnchorEl(null),
                  }}
                >
                  <MenuItem onClick={onOpenStockTransReport}>
                    Stock Transaction History
                  </MenuItem>
                  <MenuItem onClick={onOpenSalesPurchaseReport}>
                    Sales/Purchase Report
                  </MenuItem>
                  <MenuItem onClick={onOpenConsumptionReport}>
                    Consumption Report
                  </MenuItem>
                  <MenuItem onClick={onDownloadStockMaster}>
                    Stock Master
                  </MenuItem>
                  <MenuItem onClick={onDownloadServiceMaster}>
                    Service Master
                  </MenuItem>
                </Menu>
              </div>
            ) : null}
          </div>

          {/* <Grid container spacing={2}>
            <Grid
              item
              xs={4}
              sm={4}
              md={2}
              lg={1.5}
              xl={1}
              onMouseEnter={(e) => {
                setStockAnchorEl(e.currentTarget);
              }}
              onMouseLeave={() => setStockAnchorEl(null)}
            >
              <Button
                text={
                  <span
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    {stockOption}
                    <ArrowDropDownIcon
                      style={{ color: "gray", fontSize: 20 }}
                    />
                  </span>
                }
                tabSelectdBtn={activeTab === "Stock"}
                textBtn={activeTab !== "Stock"}
                color={AppColors.grayBtn2}
              />
              <Menu
                anchorEl={stockAnchorEl}
                open={Boolean(stockAnchorEl)}
                onClose={() => setStockAnchorEl(null)}
                MenuListProps={{
                  onMouseEnter: () => {},
                  onMouseLeave: () => setStockAnchorEl(null),
                }}
              >
                <MenuItem
                  onClick={() => {
                    setStockOption("Stock");
                    setActiveTab("Stock");
                    setStockAnchorEl(null);
                  }}
                >
                  Stock
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setStockOption("Dashboard");
                    setActiveTab("Stock");
                    setStockAnchorEl(null);
                  }}
                >
                  Dashboard
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setStockOption("Requests");
                    setActiveTab("Stock");
                    setStockAnchorEl(null);
                  }}
                >
                  Requests
                </MenuItem>
              </Menu>
            </Grid>
            <Grid
              item
              xs={4}
              sm={4}
              md={2}
              lg={1.5}
              xl={1}
              onMouseEnter={(e) => {
                setServiceAnchorEl(e.currentTarget);
              }}
              onMouseLeave={() => setServiceAnchorEl(null)}
            >
              <Button
                text={
                  <span
                    style={{ display: "flex", alignItems: "center", gap: 4 }}
                  >
                    {serviceOption}
                    <ArrowDropDownIcon
                      style={{ color: "gray", fontSize: 20 }}
                    />
                  </span>
                }
                tabSelectdBtn={activeTab === "Service"}
                textBtn={activeTab !== "Service"}
                color={AppColors.grayBtn2}
              />
              <Menu
                anchorEl={serviceAnchorEl}
                open={Boolean(serviceAnchorEl)}
                onClose={() => setServiceAnchorEl(null)}
                MenuListProps={{
                  onMouseEnter: () => {},
                  onMouseLeave: () => setServiceAnchorEl(null),
                }}
              >
                <MenuItem
                  onClick={() => {
                    setServiceOption("Service");
                    setActiveTab("Service");
                    setServiceAnchorEl(null);
                  }}
                >
                  Service
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setServiceOption("Dashboard");
                    setActiveTab("Service");
                    setServiceAnchorEl(null);
                  }}
                >
                  Dashboard
                </MenuItem>
              </Menu>
            </Grid>
            <Grid item xs={4} sm={4} md={2} lg={1.5} xl={1}>
              <div
                onMouseEnter={(e) => {
                  setMasterAnchorEl(e.currentTarget);
                }}
                className={`flex-center cursor ${
                  activeTab === "Master"
                    ? "pet-det-sel-tab"
                    : "pet-det-un-sel-tab"
                }`}
              >
                Master <ArrowDropDownIcon fontSize="small" />
              </div>
              <Menu
                anchorEl={masterAnchorEl}
                open={Boolean(masterAnchorEl)}
                onClose={() => setMasterAnchorEl(null)}
                MenuListProps={{
                  onMouseLeave: () => setMasterAnchorEl(null),
                }}
              >
                <MenuItem
                  onClick={() => {
                    setMasterSubTab("Location");
                    setActiveTab("Master");
                    setLocationInnerTab("Bin");
                    setMasterAnchorEl(null);
                  }}
                >
                  Location
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setMasterSubTab("Supplier");
                    setActiveTab("Master");
                    setMasterAnchorEl(null);
                  }}
                >
                  Supplier
                </MenuItem>
              </Menu>
            </Grid>
            <Grid item xs={4} sm={4} md={2} lg={1.5} xl={1}>
              <Button
                text="Purchase History"
                tabSelectdBtn={activeTab === "Purchase History"}
                textBtn={activeTab !== "Purchase History"}
                color={AppColors.grayBtn2}
                onClick={() => setActiveTab("Purchase History")}
              />
            </Grid>
          </Grid> */}
        </Container>
        {/* <div className="pos-absolute r80 top65">
          <Typography
            className="fs14 txt-semi-bold orange-color cursor"
            onClick={handlePending}
          >
            Pending {pendingStocks?.totalRecords}
          </Typography>
        </div> */}
      </TopBar>
      {subTabsToShow?.includes(activeTab) ? (
        <Grid container spacing={0}>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <div className="flex-row light-blue-back3 pv5 mh30">
              {getTabBasedSubList(activeTab)?.map((st, i) => {
                const subTabSelected =
                  activeTab === "Stock" && st === "Stock Insights"
                    ? stockInsightsList?.includes(activeSubTab)
                    : activeSubTab === st;
                return (
                  <div className="pos-rel" key={`${st}_${i}`}>
                    <Typography
                      className={`ml20 newTab ph10 cursor ${subTabSelected ? "newTabSelected blue-color" : ""}`}
                      onClick={() => {
                        if (
                          activeTab === "Stock" &&
                          st === "Stock Insights" &&
                          !stockInsightsList?.includes(activeSubTab)
                        ) {
                          onSubTabChange(stockInsightsList[0]);
                          return;
                        }
                        if (st === "Stock Insights") return;
                        onSubTabChange(st);
                      }}
                    >
                      {st}
                    </Typography>
                    {st === "Pending to Add" && (
                      <div className="red-back2 fs10 br10 img-wh20 pos-absolute white-color rhtMin5 topMin5 flex-center">
                        {pendingStocks?.totalRecords}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Grid>
        </Grid>
      ) : null}
      {activeTab === "Stock" && stockInsightsList?.includes(activeSubTab) ? (
        <Grid container spacing={0}>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <div className="flex-row light-blue-back3 pv5 mh30">
              {stockInsightsList?.map((st, i) => (
                <Typography
                  key={`${st}_${i}`}
                  className={`ml20 newTab ph10 cursor ${activeSubTab === st ? "newTabSelected blue-color" : ""}`}
                  onClick={() => onSubTabChange(st)}
                >
                  {st}
                </Typography>
              ))}
            </div>
          </Grid>
        </Grid>
      ) : null}

      <div
      // className="mt20"
      // style={{
      //   height: "calc(100vh - 130px)",
      //   overflowY: "auto",
      //   // paddingRight: "10px",
      // }}
      >
        {activeTab === "Dashboard" ? (
          <InventoryDashboard
            setActiveTab={setActiveTab}
            setActiveSubTab={setActiveSubTab}
          />
        ) : null}
        {activeTab === "Stock" ? (
          activeSubTab === "Reorder List" ? (
            <ReorderList />
          ) : activeSubTab === "Fast/Slow Moving" ? (
            <FastSlowMoving />
          ) : [
              "All Stock",
              "Low Stock",
              "Near Expiry",
              "Out of Stock",
            ].includes(activeSubTab) ? (
            <Stock
              penTblVsble={penTblVsble}
              setPenTblVsble={setPenTblVsble}
              activeSubTab={activeSubTab}
            />
          ) : // activeSubTab === "Transaction History" ? (
          //   <TransactionHistory />
          // ) : activeSubTab === "Purchase History" ? (
          //   <PurchaseHistory />
          // ) :
          null
        ) : null}
        {activeTab === "Service" ? <Service /> : null}
        {activeTab === "Purchase" ? (
          activeSubTab === "Purchase Order" ? (
            <PurchaseOrders />
          ) : activeSubTab === "Purchase Invoice" ? (
            <PurchaseInvoice />
          ) : (
            <StockPendingTable setPenTblVsble={setPenTblVsble} />
          )
        ) : null}
        {activeTab === "Adjustments & Transfers" ? (
          activeSubTab === "Transfers" ? (
            <StockRequest />
          ) : activeSubTab === "Adjustments" ? (
            <AdjustmentHistory />
          ) : (
            <PendingReview />
          )
        ) : null}
        {activeTab === "Report" && (
          <Box
            sx={{
              height: "calc(100vh - 120px)",
              overflow: "auto",
              padding: "20px",
            }}
          >
            {activeSubTab === "Stock" ? (
              <Main2 />
            ) : activeSubTab === "Service" ? (
              <Main />
            ) : null}
          </Box>
        )}
        {activeTab === "Master" &&
          (activeSubTab === "Location" ? (
            <>
              <Grid container spacing={0}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <div className="flex-row light-blue-back3 pv5 mh30">
                    {["Bin", "Location", "Category"].map((st, i) => (
                      <div className="pos-rel" key={`${st}_${i}`}>
                        <Typography
                          className={`ml20 newTab ph10 cursor ${
                            locationInnerTab === st
                              ? "newTabSelected blue-color"
                              : ""
                          }`}
                          onClick={() => setLocationInnerTab(st)}
                        >
                          {st}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </Grid>
              </Grid>
              {locationInnerTab === "Bin" ? (
                <Bin />
              ) : locationInnerTab === "Location" ? (
                <Location />
              ) : (
                <LocationCategory />
              )}
            </>
          ) : activeSubTab === "Supplier" ? (
            <Supplier />
          ) : activeSubTab === "Inventory Settings" ? (
            <>
              <Grid container spacing={0}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <div className="flex-row light-blue-back3 pv5 mh30">
                    {["Stock", "Services"].map((st, i) => (
                      <div className="pos-rel" key={`${st}_${i}`}>
                        <Typography
                          className={`ml20 newTab ph10 cursor ${
                            inventorySettingsInnerTab === st
                              ? "newTabSelected blue-color"
                              : ""
                          }`}
                          onClick={() => setInventorySettingsInnerTab(st)}
                        >
                          {st}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </Grid>
              </Grid>
              <InventorySettingsCategory
                categoryType={
                  inventorySettingsInnerTab === "Services"
                    ? "services"
                    : "stock"
                }
              />
            </>
          ) : null)}
        {/* {activeTab === "Service" &&
          (serviceOption === "Service" ? <Service /> : <Main />)}
        {activeTab === "Stock" &&
          (stockOption === "Stock" ? (
            <Stock penTblVsble={penTblVsble} setPenTblVsble={setPenTblVsble} />
          ) : stockOption === "Requests" ? (
            <StockRequest />
          ) : (
            <Main2 />
          ))}        
        {activeTab === "Purchase History" ? <PurchaseHistory /> : null} */}
      </div>

      <StockTransactionHistoryModal
        open={stockTransModVsble}
        onClose={onCloseStockTransMod}
      />

      <CustomModal
        open={salesPurchaseModValues?.visible}
        onClose={onCloseSalesPurchaseMod}
        header="Sales/Purchase Report"
        rightModal
        modalWidth={30}
      >
        <div className="mv10 mh20">
          <div className="txt-semi-bold fs14 light-grey">
            Type <span className="red-color">*</span>
          </div>
          <CustomSelect
            list={salesPurchaseTypeList}
            fullWidth
            name="type"
            handleChange={(e) =>
              onChangeSalesPurchase("type", e?.target?.value)
            }
            value={salesPurchaseModValues?.type ?? "pharmacy"}
            label=""
            select
            error={!!salesPurchaseModValues?.typeErr}
            helperText={salesPurchaseModValues?.typeErr}
          />
          <div className="txt-semi-bold fs14 light-grey mt15">Date Range</div>
          <CustomCheckbox
            radio
            value={salesPurchaseModValues?.rangeType}
            onChange={(e) =>
              onChangeSalesPurchase("rangeType", e?.target?.value)
            }
            radios={salesPurchaseRangeRadios}
          />
          {salesPurchaseModValues?.rangeType === "Custom" ? (
            <div className="flex-row-between mt10">
              <div className="w48Per">
                <div className="txt-semi-bold fs14 light-grey">
                  Start Date <span className="red-color">*</span>
                </div>
                <CustomTextField
                  value={salesPurchaseModValues?.startDate}
                  fullWidth
                  mobileDate
                  handleChange={(e) =>
                    onChangeSalesPurchase("startDate", e?.target?.value)
                  }
                  error={!!salesPurchaseModValues?.startDateErr}
                  helperText={salesPurchaseModValues?.startDateErr}
                />
              </div>
              <div className="w48Per">
                <div className="txt-semi-bold fs14 light-grey">
                  End Date <span className="red-color">*</span>
                </div>
                <CustomTextField
                  value={salesPurchaseModValues?.endDate}
                  fullWidth
                  mobileDate
                  handleChange={(e) =>
                    onChangeSalesPurchase("endDate", e?.target?.value)
                  }
                  error={!!salesPurchaseModValues?.endDateErr}
                  helperText={salesPurchaseModValues?.endDateErr}
                />
              </div>
            </div>
          ) : null}
          <div className="flex1-end mt20">
            <div>
              <CustomButton
                text="Download"
                onClick={onDownloadSalesPurchaseReport}
                smallBtn
              />
            </div>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        open={consumptionModValues?.visible}
        onClose={onCloseConsumptionMod}
        header="Consumption Report"
        modal
        modalWidth={80}
      >
        <div className="mv10 mh20">
          <div
            className="flex-row-ali-cen"
            style={{ gap: 16, flexWrap: "wrap" }}
          >
            <div>
              <div className="txt-semi-bold fs14 light-grey">Date Range</div>
              <CustomCheckbox
                radio
                isRow
                value={consumptionModValues?.period}
                onChange={(e) =>
                  onChangeConsumption("period", e?.target?.value)
                }
                radios={consumptionPeriodRadios}
              />
            </div>
            {consumptionModValues?.period === "custom" ? (
              <>
                <div style={{ width: 180 }}>
                  <div className="txt-semi-bold fs14 light-grey">
                    Start Date <span className="red-color">*</span>
                  </div>
                  <CustomTextField
                    value={consumptionModValues?.startDate}
                    fullWidth
                    mobileDate
                    handleChange={(e) =>
                      onChangeConsumption("startDate", e?.target?.value)
                    }
                    error={!!consumptionModValues?.startDateErr}
                    helperText={consumptionModValues?.startDateErr}
                  />
                </div>
                <div style={{ width: 180 }}>
                  <div className="txt-semi-bold fs14 light-grey">
                    End Date <span className="red-color">*</span>
                  </div>
                  <CustomTextField
                    value={consumptionModValues?.endDate}
                    fullWidth
                    mobileDate
                    handleChange={(e) =>
                      onChangeConsumption("endDate", e?.target?.value)
                    }
                    error={!!consumptionModValues?.endDateErr}
                    helperText={consumptionModValues?.endDateErr}
                  />
                </div>
              </>
            ) : null}
            <div className="flex1-end">
              <div>
                <CustomButton
                  text="⬇"
                  onClick={onDownloadConsumptionReport}
                  smallBtn
                />
              </div>
            </div>
          </div>
          {consumptionModValues?.report ? (
            <ConsumptionReportResult
              report={consumptionModValues.report}
              section={consumptionModValues?.section}
              onChangeSection={(section) =>
                onChangeConsumption("section", section)
              }
            />
          ) : null}
        </div>
      </CustomModal>
    </>
  );
};

export default Inventory;
